import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// ✅ FIX: Use a single default import
import courseService from '../../services/courseService';
import type { ICourse, ICourseModule } from '../../types/course';
import Loader from '../../components/common/Loader';
import { showToast } from '../../utils/showToast';
import { BookOpen, DollarSign, Clock, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import enrollmentService from '../../services/enrollmentService';

const CourseDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [course, setCourse] = useState<ICourse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!slug) return;
      setIsLoading(true);
      try {
        // ✅ FIX: Call the function from the imported service object
        const publicCourseData = await courseService.getCourseBySlug(slug);
        setCourse(publicCourseData);

        if (isAuthenticated && user) {
          if (user.isAdmin) {
            const fullCourseData = await courseService.getCourseForEdit(publicCourseData._id);
            setCourse(fullCourseData);
            setIsEnrolled(true);
          } else {
            const myEnrollments = await enrollmentService.getMyEnrollments();
            const isUserEnrolled = myEnrollments.some(e => e.course._id === publicCourseData._id);
            setIsEnrolled(isUserEnrolled);
            if (isUserEnrolled) {
              const fullCourseData = await courseService.getCourseForEdit(publicCourseData._id);
              setCourse(fullCourseData);
            }
          }
        }
      } catch (err: any) {
        showToast(err.message, { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    if (!isAuthLoading) {
      fetchCourse();
    }
  }, [slug, isAuthenticated, user, isAuthLoading]);

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModuleId(prevId => (prevId === moduleId ? null : moduleId));
  };

  if (isAuthLoading || isLoading) {
    return <div className="flex justify-center items-center h-screen"><Loader /></div>;
  }

  if (!course) {
    return <div className="text-center py-10">Course not found.</div>;
  }

  return (
    <div className="py-10 px-4">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {course.image && (
          <img src={course.image} alt={course.title} className="w-full h-72 object-cover rounded-lg mb-6" />
        )}
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{course.title}</h1>
        <div className="flex flex-wrap items-center text-sm text-gray-500 mb-6 gap-x-4 gap-y-2">
          <div className="flex items-center"><BookOpen className="w-4 h-4 mr-2" />Instructor: {course.instructorName}</div>
          <div className="flex items-center"><Clock className="w-4 h-4 mr-2" />Duration: {course.duration}</div>
          <div className="flex items-center"><DollarSign className="w-4 h-4 mr-2" />Price: {course.price ? `₹${course.price.toLocaleString()}` : 'Contact for price'}</div>
        </div>
        <p className="text-gray-700 mb-6">{course.description}</p>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Modules</h2>
        {isEnrolled ? (
          course.modules?.length > 0 ? (
            <div className="space-y-3">
              {course.modules.map((moduleItem: ICourseModule) => (
                <div key={moduleItem._id} className="border rounded-lg overflow-hidden">
                  <div className="flex justify-between items-center p-4 cursor-pointer bg-gray-50 hover:bg-gray-100" onClick={() => toggleModuleExpansion(moduleItem._id!)}>
                    <h3 className="text-lg font-semibold">{moduleItem.title}</h3>
                    <span>{expandedModuleId === moduleItem._id ? '▲' : '▼'}</span>
                  </div>
                  {expandedModuleId === moduleItem._id && (
                    <div className="p-4 border-t">
                      <p className="text-gray-700 mb-3">{moduleItem.description}</p>
                      {moduleItem.materials?.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-semibold">Materials:</h4>
                          {moduleItem.materials.map((material, materialIndex) => (
                            <button
                              key={materialIndex}
                              // ✅ FIX: Call the function from the service object
                              onClick={() => courseService.getSecureMaterial(course._id, moduleItem._id!, materialIndex)}
                              className="flex items-center text-sm text-blue-600 hover:underline"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              {material.name} ({material.type.toUpperCase()})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No modules available for this course yet.</p>
          )
        ) : (
          <div className="p-6 border-2 border-dashed rounded-lg text-center">
            <p className="text-gray-600">You must be enrolled to view course modules.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetails;